import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Tooltip,
  CircularProgress,
  MenuItem,
  TextField,
  Divider,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Collapse,
  IconButton,
  Paper,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useLocation } from "react-router-dom";

import { PageTitle } from "../../components-new/common";
import SimilarStepsTable from "../../components-new/common/SimilarStepsTable";
import UpdateStepsDialog from "./UpdateStepsDialog";
import {
  useGetSimilarStepsQuery,
  useBulkUpdateSimilarStepsMutation,
  useGetAttributesQuery,
  useClearRemainingTCsMutation,
} from "../../useQuery";
import { useNotification } from "../../hooks";
import {
  Plus,
  X as RemoveIcon,
  ChevronDown,
  ChevronUp,
  Search,
  SlidersHorizontal,
} from "lucide-react";
import {
  DEFAULT_CONDITION,
  OPERATOR_OPTIONS,
  QUERY_FIELD_OPTIONS,
  QUERY_FIELD_PARAM_MAP,
} from "../../constants/similarsteps";
import { buildAdvancedQuery } from "../../utils/similarStepsQueryBuilder";
import { generateId } from "../../utils/helpers";

const PageWrapper = styled(Box)({
  height: "100vh",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#f5f7f9",
  overflow: "hidden",
});

const Header = styled(Box)({
  flexShrink: 0,
  backgroundColor: "#ffffff",
  borderBottom: "1px solid #E2E6F3",
  padding: "0 24px",
});

const HeaderTop = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: 60,
  gap: 16,
});

const HeaderMeta = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 10,
  flex: 1,
  minWidth: 0,
});

const StepIdBadge = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  padding: "3px 10px",
  backgroundColor: "#f0f4ff",
  border: "1px solid #d1dbff",
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  color: "#547de8",
  whiteSpace: "nowrap",
});

const QueryBuilderPanel = styled(Box)(({ expanded }) => ({
  flexShrink: 0,
  backgroundColor: "#ffffff",
  borderBottom: expanded ? "1px solid #E2E6F3" : "none",
  overflow: "hidden",
}));

const QueryBuilderToggle = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 24px",
  cursor: "pointer",
  backgroundColor: "#f8f9fc",
  borderTop: "1px solid #E2E6F3",
  borderBottom: "1px solid #E2E6F3",
  "&:hover": { backgroundColor: "#f0f4ff" },
  transition: "background 0.15s ease",
  userSelect: "none",
});

const QueryBuilderBody = styled(Box)({
  padding: "16px 24px 20px",
});

const ConditionRowWrapper = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  flexWrap: "wrap",
  padding: "10px 12px",
  backgroundColor: "#f8f9fc",
  border: "1px solid #E2E6F3",
  borderRadius: 8,
  marginBottom: 8,
  transition: "border-color 0.15s ease",
  "&:hover": { borderColor: "#d1dbff" },
});

const ConditionSelect = styled(Select)({
  fontSize: "14px",
  color: "#2b3539",
  backgroundColor: "#ffffff",
  borderRadius: 0,
  "& .MuiSelect-select": { padding: "7px 12px" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E2E6F3", borderRadius: 0 },
  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#547de8" },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#547de8" },
});

const ConditionInput = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    fontSize: "14px",
    color: "#2b3539",
    backgroundColor: "#ffffff",
    borderRadius: 6,
    "& fieldset": { borderColor: "#E2E6F3" },
    "&:hover fieldset": { borderColor: "#547de8" },
    "&.Mui-focused fieldset": { borderColor: "#547de8" },
  },
  "& .MuiInputBase-input": { padding: "7px 12px" },
});

const LogicToggle = styled(ToggleButtonGroup)({
  height: 32,
  "& .MuiToggleButton-root": {
    padding: "0 10px",
    fontSize: "14px",
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: "#8089A8",
    border: "1px solid #E2E6F3",
    textTransform: "uppercase",
    lineHeight: 1,
    borderRadius: "0px !important",
    "&.Mui-selected": {
      backgroundColor: "#547de8",
      color: "#ffffff",
      borderColor: "#547de8",
      "&:hover": { backgroundColor: "#3b60c4" },
    },
    "&:not(.Mui-selected):hover": { backgroundColor: "#f0f4ff" },
  },
});

const PrimaryButton = styled(Button)({
  backgroundColor: "#547de8",
  color: "#ffffff",
  height: 36,
  textTransform: "none",
  fontWeight: 600,
  fontSize: "13px",
  borderRadius: 0,
  paddingLeft: 16,
  paddingRight: 16,
  boxShadow: "none",
  "&:hover": { backgroundColor: "#3b60c4", boxShadow: "none" },
  "&:disabled": { backgroundColor: "#c7d4fb", color: "#fff" },
});

const SecondaryButton = styled(Button)({
  color: "#4b5563",
  background: "#ffffff",
  height: 36,
  fontSize: "14px",
  border: "1px solid #E2E6F3",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 0,
  paddingLeft: 14,
  paddingRight: 14,
  "&:hover": { background: "#f5f7f9", borderColor: "#d1dbff" },
  "&:disabled": { color: "#c0c0c0" },
});

const DangerButton = styled(Button)({
  color: "#ef4444",
  background: "transparent",
  height: 36,
  fontSize: "14px",
  textTransform: "none",
  fontWeight: 500,
  borderRadius: 0,
  "&:hover": { background: "#fef2f2" },
});

const ContentArea = styled(Box)({
  flex: 1,
  overflowY: "auto",
  padding: "20px 24px",
});

const MatchedCasesCard = styled(Paper)({
  background: "#f8f9fc",
  border: "1px solid #E2E6F3",
  borderRadius: 10,
  padding: "14px 18px",
  marginBottom: 16,
  boxShadow: "none",
});

const EmptyStateBox = styled(Box)({
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  flex: 1,
  gap: 10,
  color: "#8089A8",
});

const SimilarTestSteps = () => {
  const location = useLocation();
  const notify = useNotification();

  const searchParams = new URLSearchParams(location.search);
  const stepId = searchParams.get("stepId");
  const testSuiteId = searchParams.get("testSuiteId");

  const [selectedItems, setSelectedItems] = useState([]);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const [conditions, setConditions] = useState([{ ...DEFAULT_CONDITION }]);
  const [activeConditions, setActiveConditions] = useState(null);
  const [queryPanelOpen, setQueryPanelOpen] = useState(true);

  const { data: attributesData } = useGetAttributesQuery(stepId, { enabled: !!stepId });
  const attributesMap = attributesData?.attributes ?? {};
  const attributeKeys = Object.keys(attributesMap);

  const advancedQueryPayload = buildAdvancedQuery(activeConditions, attributesMap);
  const advancedQueryParam = advancedQueryPayload ? JSON.stringify(advancedQueryPayload) : null;
  const isQueryEnabled = !!stepId && !!testSuiteId && !!advancedQueryParam;

  const {
    data: similarStepsData,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetSimilarStepsQuery(
    {
      testStepId: stepId,
      testSuiteId,
      ...(advancedQueryParam ? { advancedQuery: advancedQueryParam } : {}),
    },
    { enabled: isQueryEnabled },
  );

  const steps = similarStepsData?.data ?? [];
  const matchedTestCases = similarStepsData?.matchedTestCases ?? [];

  const { mutate: bulkUpdateSteps, isPending: isUpdating } = useBulkUpdateSimilarStepsMutation({
    onSuccess: () => {
      notify("Test steps updated successfully", "success");
      setUpdateDialogOpen(false);
      refetch();
    },
    onError: (err) => {
      notify(
        err?.response?.data?.message ?? err?.message ?? "Failed to update test steps",
        "error",
      );
    },
  });

  const { mutate: clearRemainingTC, isPending: isClearing } = useClearRemainingTCsMutation({
    onSuccess: () => {
      notify("Remaining TCs reset successfully", "success");
    },
    onError: (err) => {
      notify(
        err?.response?.data?.message ?? err?.message ?? "Failed to reset remaining TCs",
        "error",
      );
    },
  });

  const addCondition = () =>
    setConditions((prev) => [
      ...prev,
      { ...DEFAULT_CONDITION, id: generateId(), logicalOp: "AND" },
    ]);

  const removeCondition = (id) => setConditions((prev) => prev.filter((c) => c.id !== id));

  const updateCondition = (id, key, val) =>
    setConditions((prev) => prev.map((c) => (c.id === id ? { ...c, [key]: val } : c)));

  const toggleLogicOp = (id, newOp) => {
    if (newOp) updateCondition(id, "logicalOp", newOp);
  };

  const isConditionValid = (c) =>
    c.field === "outerHtml"
      ? Array.isArray(c.attributeKey) && c.attributeKey.length > 0
      : c.useSourceValue !== false || c.value.trim() !== "";

  const runQuery = () => {
    if (!conditions.length || !conditions.every(isConditionValid)) return;
    setActiveConditions([...conditions]);
  };

  const clearQuery = () => {
    setConditions([{ ...DEFAULT_CONDITION, id: generateId() }]);
    setActiveConditions(null);
  };

  const handleSelectAll = (e) =>
    setSelectedItems(e.target.checked ? steps.map((s) => s.idtest_step ?? s.Step_Number) : []);

  const handleSelectItem = (id) =>
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));

  const handleUpdateClick = () => {
    setUpdateDialogOpen(true);
  };

  const updateField = () => {
    bulkUpdateSteps({
      testSuiteId: Number(testSuiteId),
      testStepId: Number(stepId),
      testStepIds: selectedItems,
      nonMatchedTestCases: similarStepsData?.nonMatchedTestCases ?? [],
    });
  };

  const activeCount = activeConditions?.length ?? 0;
  const hasActiveQuery = activeCount > 0;
  const isRunQueryDisabled = conditions.length === 0 || !conditions.every(isConditionValid);

  return (
    <PageWrapper>
      <Header>
        <HeaderTop>
          <HeaderMeta>
            <PageTitle title="Find Similar Test Steps" variant="h5" truncate maxWidth="100%" />

            {stepId && (
              <Tooltip title={`Source Step ID: ${stepId}`} placement="bottom">
                <StepIdBadge>Step #{stepId}</StepIdBadge>
              </Tooltip>
            )}
          </HeaderMeta>

          <Box display="flex" gap={1.5} alignItems="center">
            {hasActiveQuery && testSuiteId && (
              <SecondaryButton
                disabled={isClearing}
                onClick={() => clearRemainingTC({ testSuiteId })}
              >
                {isClearing ? (
                  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
                ) : null}
                Reset Remaining TCs
              </SecondaryButton>
            )}
            {selectedItems.length > 0 && (
              <PrimaryButton variant="contained" onClick={handleUpdateClick}>
                Update {selectedItems.length} step{selectedItems.length !== 1 ? "s" : ""}
              </PrimaryButton>
            )}
          </Box>
        </HeaderTop>
      </Header>

      <QueryBuilderPanel expanded={queryPanelOpen ? 1 : 0}>
        <QueryBuilderToggle onClick={() => setQueryPanelOpen((v) => !v)}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <SlidersHorizontal size={15} color="#547de8" />
            <Typography fontSize="14px" fontWeight={600} color="#2b3539">
              Query Builder
            </Typography>
            <Typography fontSize="14px" fontWeight={500} color="#8089A8">
              Build conditions to find matching steps
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1.5}>
            <IconButton size="small" sx={{ color: "#8089A8" }}>
              {queryPanelOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </IconButton>
          </Box>
        </QueryBuilderToggle>

        <Collapse in={queryPanelOpen}>
          <QueryBuilderBody>
            <Box mb={2}>
              {conditions.map((condition, index) => (
                <ConditionRowWrapper key={condition.id}>
                  {index === 0 ? (
                    <Box
                      sx={{
                        width: 68,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Typography
                        fontSize="14px"
                        fontWeight={700}
                        color="#8089A8"
                        letterSpacing="0.06em"
                      >
                        WHERE
                      </Typography>
                    </Box>
                  ) : (
                    <LogicToggle
                      value={condition.logicalOp}
                      exclusive
                      onChange={(_, newOp) => toggleLogicOp(condition.id, newOp)}
                      size="small"
                      sx={{ flexShrink: 0 }}
                    >
                      <ToggleButton value="AND">AND</ToggleButton>
                      <ToggleButton value="OR">OR</ToggleButton>
                    </LogicToggle>
                  )}

                  <ConditionSelect
                    value={condition.field}
                    onChange={(e) => {
                      updateCondition(condition.id, "field", e.target.value);
                      updateCondition(condition.id, "attributeKey", []);
                    }}
                    sx={{ minWidth: 170 }}
                  >
                    {QUERY_FIELD_OPTIONS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "14px" }}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </ConditionSelect>

                  {condition.field === "outerHtml" && (
                    <Select
                      multiple
                      value={Array.isArray(condition.attributeKey) ? condition.attributeKey : []}
                      onChange={(e) =>
                        updateCondition(condition.id, "attributeKey", e.target.value)
                      }
                      sx={{
                        minWidth: 200,
                        maxWidth: 280,
                        fontSize: "14px",
                        borderRadius: 0,
                        "& .MuiSelect-select": { padding: "7px 12px" },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#E2E6F3",
                          borderRadius: 0,
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#547de8" },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#547de8",
                        },
                      }}
                      renderValue={(selected) =>
                        selected.length === 0 ? (
                          <Typography sx={{ fontSize: "14px", color: "#8089A8" }}>
                            Select attributes...
                          </Typography>
                        ) : (
                          selected.join(", ")
                        )
                      }
                    >
                      {attributeKeys.map((key) => (
                        <MenuItem key={key} value={key} sx={{ fontSize: "14px" }}>
                          <Box display="flex" justifyContent="space-between" width="100%" gap={2}>
                            <span>{key}</span>
                            {attributesMap[key] && (
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: "14px",
                                  color: "#8089A8",
                                  fontStyle: "italic",
                                  maxWidth: 140,
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {attributesMap[key]}
                              </Typography>
                            )}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}

                  {/* Operator selector */}
                  {condition.field !== "outerHtml" && (
                    <ConditionSelect
                      value={condition.operator}
                      onChange={(e) => updateCondition(condition.id, "operator", e.target.value)}
                      sx={{ minWidth: 155, color: "#547de8" }}
                    >
                      {OPERATOR_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: "14px" }}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </ConditionSelect>
                  )}

                  <ConditionSelect
                    value={condition.useSourceValue !== false ? "source" : "custom"}
                    onChange={(e) => {
                      const isSource = e.target.value === "source";
                      updateCondition(condition.id, "useSourceValue", isSource);
                      if (isSource) updateCondition(condition.id, "value", "");
                    }}
                    sx={{ minWidth: 145 }}
                  >
                    <MenuItem value="source" sx={{ fontSize: "14px" }}>
                      Use Step Value
                    </MenuItem>
                    <MenuItem value="custom" sx={{ fontSize: "14px" }}>
                      Custom Value
                    </MenuItem>
                  </ConditionSelect>

                  {/* Custom value input */}
                  {condition.useSourceValue === false && (
                    <ConditionInput
                      placeholder="Enter value..."
                      value={condition.value}
                      onChange={(e) => updateCondition(condition.id, "value", e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && runQuery()}
                      sx={{ flex: 1, minWidth: 150 }}
                    />
                  )}

                  {conditions.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeCondition(condition.id)}
                      sx={{
                        color: "#ef4444",
                        border: "1px solid #fca5a5",
                        borderRadius: 0,
                        padding: "5px",
                        "&:hover": { background: "#fef2f2" },
                      }}
                    >
                      <RemoveIcon size={14} />
                    </IconButton>
                  )}
                </ConditionRowWrapper>
              ))}
            </Box>

            <Box display="flex" alignItems="center" gap={1.5}>
              <SecondaryButton onClick={addCondition} startIcon={<Plus size={14} />}>
                Add Condition
              </SecondaryButton>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

              <PrimaryButton
                onClick={runQuery}
                disabled={isRunQueryDisabled}
                startIcon={<Search size={14} />}
                sx={{ fontSize: "14px" }}
              >
                Run Query
              </PrimaryButton>

              {hasActiveQuery && (
                <>
                  <DangerButton size="small" onClick={clearQuery}>
                    Clear
                  </DangerButton>
                </>
              )}
            </Box>
          </QueryBuilderBody>
        </Collapse>
      </QueryBuilderPanel>

      {isFetching && (
        <EmptyStateBox>
          <CircularProgress size={32} sx={{ color: "#547de8" }} />
          <Typography fontSize="13px" fontWeight={500} color="#8089A8">
            Searching for similar steps...
          </Typography>
        </EmptyStateBox>
      )}

      {isError && (
        <EmptyStateBox>
          <Typography color="error" fontSize="13px" fontWeight={500}>
            {error?.message ?? "Failed to fetch similar steps"}
          </Typography>
        </EmptyStateBox>
      )}

      {!isFetching && !isError && !steps.length && (
        <EmptyStateBox>
          <Search size={32} color="#d1dbff" />
          <Typography fontSize="13px" fontWeight={500} color="#8089A8">
            {activeCount === 0
              ? "Run a query to search for similar steps"
              : "No matching steps found"}
          </Typography>
          {activeCount > 0 && (
            <Typography fontSize="13px" fontWeight={500} color="#b0b8cc">
              Try adjusting your conditions or switching operators
            </Typography>
          )}
        </EmptyStateBox>
      )}

      {!isFetching && !isError && steps.length > 0 && (
        <ContentArea>
          {matchedTestCases.length > 0 && (
            <MatchedCasesCard>
              <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                <Typography fontSize="13px" fontWeight={600} color="#2b3539">
                  Matched Test Cases
                </Typography>
                <Chip
                  label={matchedTestCases.length}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: 12,
                    fontWeight: 700,
                    backgroundColor: "#e0e7ff",
                    color: "#4338ca",
                  }}
                />
              </Box>
              <Box display="flex" flexDirection="column" gap={0.5}>
                {matchedTestCases.map((tc) => (
                  <Typography key={tc.testCaseId} fontSize="13px" color="#4b5563">
                    {tc.productName} / {tc.moduleName} / {tc.testCaseName}
                    <Typography component="span" fontSize="13px" color="#8089A8">
                      {" "}
                      (ID: {tc.testCaseId})
                    </Typography>
                  </Typography>
                ))}
              </Box>
            </MatchedCasesCard>
          )}

          <SimilarStepsTable
            data={steps}
            selectedItems={selectedItems}
            onSelectAll={handleSelectAll}
            onSelectItem={handleSelectItem}
            activeConditions={activeConditions}
          />
        </ContentArea>
      )}

      <UpdateStepsDialog
        open={updateDialogOpen}
        onClose={() => setUpdateDialogOpen(false)}
        selectedItems={selectedItems}
        isUpdating={isUpdating}
        onUpdate={updateField}
      />
    </PageWrapper>
  );
};

export default SimilarTestSteps;
